import { Component, inject, Inject, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { PlatformUser } from '../../core/data-models/platform-user';
import { Tenant } from '../../core/data-models/tenant';
import { UnitBalanceService } from '../../core/services/unit-balance.service';
import { ModuleName } from '../enums/ModuleName';
import { TranslatePipe } from '../pipes/translate.pipe';
import { inOutAnimation } from '../animations/allAnimations';
import { ZenSnackbarService, ZenSnackBarTypes } from '../Theme/services/zen-snackbar.service';
import { PushidService } from '@ex/module/core/services/pushid.service';
import { creditTypeOf, PRODUCT_CATALOG, ProductCatalogEntry } from 'src/app/common/account-overview/data-access/product-catalog';

@Component({
  selector: 'app-credit-balance-dialog',
  templateUrl: './credit-balance-dialog.component.html',
  styleUrls: ['./credit-balance-dialog.component.scss'],
  animations: [inOutAnimation]
})
export class CreditBalanceDialogComponent implements OnInit {
  pushIdService = inject(PushidService);
  productCatalog = PRODUCT_CATALOG;
  /** Amount typed per product, keyed by ProductCatalogEntry.key. Empty inputs stay ''. */
  allUnits: { [productKey: string]: string } = {};
  ModuleNames = ModuleName
  currentBalance;
  surveyRequires: number;
  entity: any;
  tenantKey: string;
  language: string;
  user: PlatformUser;
  tenant: Tenant;
  submittedFrom: string;
  requestType: string;
  requestSent: boolean = false;
  constructor(@Inject(MAT_DIALOG_DATA) public data, private dialogRef: MatDialogRef<CreditBalanceDialogComponent>,
    private zenSnackBarService: ZenSnackbarService,
    private unitBalanceService: UnitBalanceService, private translationPipe: TranslatePipe) { }

  ngOnInit(): void {
    this.currentBalance = this.data.currentBalance;
    this.surveyRequires = this.data.surveyRequires;
    this.user = this.data.user;
    this.entity = this.data.entity;
    this.tenantKey = this.data.tenantKey;
    this.tenant = this.data.tenant;
    this.language = this.data.language;
    this.submittedFrom = this.data.submittedFrom;
    this.requestType = this.data.requestType;

    if (!this.requestType)
      this.requestType = '';

    if (!this.submittedFrom)
      this.submittedFrom = 'OTHER';
  }

  /**
   * Submits one request per product the user entered an amount for.
   *
   * Every role uses this path: the amount inputs are per-product for everyone, so the
   * old split (all-products for Super Admin, a single `units` field for everyone else)
   * had nothing left to drive it.
   */
  async requestAllUnits() {
    const requested = this.productCatalog.filter(product => Number(this.allUnits[product.key]) >= 1);

    if (!requested.length)
      return this.zenSnackBarService.snackBar({ message: this.translationPipe.transform("noUnits", this.language, this.ModuleNames.Shared), type: ZenSnackBarTypes.Error });

    await Promise.all(requested.map(product => this.submitUnitRequest(product, Number(this.allUnits[product.key]))));

    if (this.user.role == 'User') {
      this.zenSnackBarService.snackBar({ message: this.translationPipe.transform("txtRequestSent", this.language, this.ModuleNames.Shared), type: ZenSnackBarTypes.Success });
      this.dialogRef.close();
      return;
    }

    this.requestSent = true;
  }

  private submitUnitRequest(product: ProductCatalogEntry, units: number): Promise<any> {
    const creditType = creditTypeOf(product.key);
    const creditTypeName = product.requestName;

    // A plain user asks the super admin; no TenantUnitRequests record is written.
    if (this.user.role == 'User') {
      this.unitBalanceService.requestMoreUnitsFromSuperAdmin(
        { user: this.user, tenant: this.tenant, entityName: this.entity.name, units, creditType }).subscribe();
      return Promise.resolve();
    }

    const unitRequest = {
      key: this.pushIdService.generatePushId(), tenantKey: this.tenantKey,
      entityName: this.entity.name, units, creditType: creditTypeName
    };

    return this.unitBalanceService.updateUnitBalance(unitRequest).then(() => {
      this.unitBalanceService.requestMoreUnitsFromTenantAdmin({
        user: this.user, tenant: this.tenant, entityName: this.entity.name,
        units, creditType, surveyName: creditTypeName
      }).subscribe();
    });
  }

  /** Empty inputs arrive as '', which would string-concat against a balance. */
  toNumber(value: any): number {
    return Number(value) || 0;
  }

  returnNumber() {
    return this.productCatalog.reduce((total, product) => total + this.toNumber(this.allUnits[product.key]), 0);
  }

}
